/**
 * V2 Geography — an explorer's visited world, at its own shareable URL
 * (/adventure/user/{username}/map). Reuses V1's choropleth module
 * (assets/js/geography-map.js: world + USA tabs, visited coloring,
 * tooltips) and V1's stats aggregation (location-service), with a
 * native <details> drill-down: country → state → county with counts.
 */
import * as gateway from '../../core/gateway.js';
import { cached } from '../../core/query.js';
import { drainRows } from '../../core/drain.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import {
  renderGeographyMaps,
  injectGeographyMapStyles,
} from '../../../assets/js/geography-map.js';
import {
  calculateLocationStats,
  formatLocationStats,
  buildNestedLocationList,
  backfillMissingGeoData,
} from '../../../assets/js/location-service.js';
import { shareButton, handleShareClick } from '../../ui/share.js';
import { fetchProfile } from './profile.js';
import { regionParam } from './region.js';
import { setContext as setRibbonContext } from '../../ui/visitor-ribbon.js';

/** The user's yoinks with just the geo columns (distinct work happens in stats). */
async function fetchGeoRows(userId) {
  return cached(`geo-rows:${userId}`, async () => {
    // Drained past the old silent 1000-row cap (assessment 1.5): the
    // choropleth was quietly undercounting prolific explorers
    return drainRows(gateway.TABLES.YOINKS, {
      select: 'id,location_id,locations(id,display_name,county,state_province,country,country_code,lat,lng,google_place_id)',
      filters: { user_id: `eq.${userId}` },
    });
  });
}

/**
 * Nested drill-down as native details/summary (keyboard + SR for free).
 * Every region links into the journal narrowed to it (V1's zoom-filter
 * buttons, un-deferred 2026-07-16): opening a level reveals its "All N
 * yoinks in X" link, and county rows link directly.
 * @param {object} stats - from calculateLocationStats
 * @param {string} journalPath - href base, e.g. /adventure/user/ada
 */
export function drillDownHtml(stats, journalPath = '') {
  const nested = buildNestedLocationList(stats);
  if (!nested.length) return '';
  const regionLink = (region, label) => {
    const param = regionParam(region);
    return `<a class="geo-region-link" data-region="${escapeHtml(param)}"
      href="${journalPath}?region=${encodeURIComponent(param)}">${label}</a>`;
  };
  const allLink = (region, count) =>
    regionLink(region, `All ${count} yoink${count === 1 ? '' : 's'} in ${escapeHtml(region.county || region.state || region.country)} →`);
  return nested
    .map((country) => `
      <details class="geo-country">
        <summary>${escapeHtml(country.name)} <span class="geo-count">${country.yoinkCount}</span></summary>
        ${allLink({ country: country.name }, country.yoinkCount)}
        ${(country.states || [])
          .map((state) => `
          <details class="geo-state">
            <summary>${escapeHtml(state.name)} <span class="geo-count">${state.yoinkCount}</span></summary>
            ${allLink({ country: country.name, state: state.name }, state.yoinkCount)}
            <ul class="geo-counties">
              ${(state.counties || [])
                .map((county) => `<li>${regionLink(
                  { country: country.name, state: state.name, county },
                  `${escapeHtml(county)} →`
                )}</li>`)
                .join('')}
            </ul>
          </details>`)
          .join('')}
      </details>`)
    .join('');
}

export function mount(el, ctx) {
  const { router, params } = ctx;
  let destroyed = false;
  let username = null;

  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';

  el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';

  (async () => {
    try {
      const profile = await fetchProfile(params.username);
      if (destroyed) return;
      if (!profile) {
        el.innerHTML = '<div class="v2-empty">No explorer by that name.</div>';
        return;
      }
      username = profile.username;
      const name = profile.full_name || profile.username;
      document.title = `${name}’s geography | Yoink Adventures v2`;
      setRibbonContext(`${name} has pinned every place they’ve been. Start your own travel map.`);

      const rows = await fetchGeoRows(profile.id);
      if (destroyed) return;
      const stats = calculateLocationStats(rows);

      el.innerHTML = `
        <nav class="v2-back" aria-label="Back">
          <a href="${basePath}/user/${escapeHtml(username)}" data-geo-back>← ${escapeHtml(name)}’s journal</a>
          ${shareButton(`${name}’s geography on Yoink Adventures`)}
        </nav>
        <header class="v2-category-head">
          <div class="cat-head-names">
            <h2 class="cat-head-name">${escapeHtml(name)}’s geography</h2>
            <p class="cat-head-desc">${escapeHtml(formatLocationStats(stats) || 'Nowhere yet — the map is waiting.')}</p>
          </div>
        </header>
        <div class="geo-maps" data-geo-maps></div>
        <section class="journal-follows" aria-label="Places by region">
          <h3 class="friends-title">By region</h3>
          <div class="geo-drilldown">${
            drillDownHtml(stats, `${basePath}/user/${encodeURIComponent(username)}`) ||
            '<p class="log-hint">Log a yoink to start coloring the map.</p>'
          }</div>
        </section>`;

      // V1's lazy backfill: quietly fill missing county/state/country for
      // locations that have coordinates (writes via the shared gateway)
      backfillMissingGeoData(rows.map((r) => r.locations).filter(Boolean)).catch(() => {});

      injectGeographyMapStyles();
      // V1 module renders the world/USA choropleths into the container;
      // no tabs in V2 — both maps stack (2026-07-12 feedback round)
      renderGeographyMaps(el.querySelector('[data-geo-maps]'), stats, { tabs: false }).catch(() => {
        const host = el.querySelector('[data-geo-maps]');
        if (host) host.innerHTML = '<p class="log-hint">The map images couldn’t load — the region list below has everything.</p>';
      });
    } catch {
      if (destroyed) return;
      el.innerHTML = '<div class="v2-error">Couldn’t reach the journal.</div>';
    }
  })();

  function onClick(event) {
    if (handleShareClick(event)) return;
    const region = event.target.closest('[data-region]');
    if (region) {
      // SPA hop to the journal filtered to this region (the href stays
      // real for open-in-new-tab)
      event.preventDefault();
      router.navigate('user', { username }, { query: { region: region.dataset.region } });
      return;
    }
    const back = event.target.closest('[data-geo-back]');
    if (back) {
      event.preventDefault();
      router.navigate('user', { username });
    }
  }

  el.addEventListener('click', onClick);

  return () => {
    destroyed = true;
    el.removeEventListener('click', onClick);
  };
}
